import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';

// Auth0
import { useAuth0 } from '@auth0/auth0-react';

// Apollo
import { ApolloProvider, ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import toast from 'react-hot-toast';

import { BASE_API_URL } from '../lib/utils';
import { useLogout } from '../components/domains/auth/auth.service';
import { Loader } from '../components/_core/loader.component';
import { tokenVar } from './token.service';

let loggingLinkCount = 0;
let clientRecreationCount = 0;

export const RIApolloProvider = ({ children }: PropsWithChildren) => {
  const [client, setClient] = useState<ApolloClient<unknown> | null>(null);
  const [clientIsAuthenticated, setClientIsAuthenticated] = useState(false);
  const [requiresAuthClient, setRequiresAuthClient] = useState<'yes' | 'no' | null>(null);
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const { handleLogout } = useLogout();

  // Track previous values for debugging
  const prevDepsRef = useRef({ isAuthenticated, isLoading, handleLogout, getAccessTokenSilently });

  useEffect(() => {
    if (!isLoading) {
      const newValue = isAuthenticated ? 'yes' : 'no';
      if (requiresAuthClient !== newValue) {
        console.log('[GraphQL Debug] requiresAuthClient changing:', { from: requiresAuthClient, to: newValue });
      }
      setRequiresAuthClient(newValue);
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    // VERBOSE DEBUGGING - Track what triggered this effect
    clientRecreationCount++;
    const timestamp = new Date().toISOString();
    console.log(`[GraphQL Debug] ========== Client Recreation #${clientRecreationCount} at ${timestamp} ==========`);

    // Check which dependencies changed
    const prevDeps = prevDepsRef.current;
    const changedDeps: string[] = [];

    if (prevDeps.isAuthenticated !== isAuthenticated) {
      console.log('[GraphQL Debug] isAuthenticated changed:', { from: prevDeps.isAuthenticated, to: isAuthenticated });
      changedDeps.push('isAuthenticated');
    }
    if (prevDeps.isLoading !== isLoading) {
      console.log('[GraphQL Debug] isLoading changed:', { from: prevDeps.isLoading, to: isLoading });
      changedDeps.push('isLoading');
    }
    if (prevDeps.handleLogout !== handleLogout) {
      console.log('[GraphQL Debug] handleLogout reference changed (function recreated)');
      changedDeps.push('handleLogout');
    }
    if (prevDeps.getAccessTokenSilently !== getAccessTokenSilently) {
      console.log('[GraphQL Debug] getAccessTokenSilently reference changed (function recreated)');
      changedDeps.push('getAccessTokenSilently');
    }

    if (changedDeps.length === 0) {
      console.log('[GraphQL Debug] WARNING: Effect ran but no dependencies changed! This is the FIRST render.');
    } else {
      console.log('[GraphQL Debug] Changed dependencies:', changedDeps.join(', '));
    }

    // Update tracked values
    prevDepsRef.current = { isAuthenticated, isLoading, handleLogout, getAccessTokenSilently };

    const httpLink = createHttpLink({
      uri: `${BASE_API_URL}/graphql`
    });

    const removeTypenameLink = removeTypenameFromVariables();

    const authLink = setContext(async (_, { headers }) => {
      const options = { headers: { ...headers } };
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();

          if (token) {
            options.headers.Authorization = `Bearer ${token}`;
            tokenVar(options.headers.Authorization);
          }
        } catch (error) {
          await handleLogout({
            afterExecution: () =>
              toast.error('Re-authentication required, please log in again.', { duration: 5000, id: 'unauthenticated' })
          });
        }
      }

      return options;
    });

    const isDev = import.meta.env.VITE_ENV === 'dev';

    const loggingLink = new ApolloLink((operation, forward) => {
      if (forward == null) throw new Error('Must not be a terminating link');

      const startTime = Date.now();
      const currentRequestNum = loggingLinkCount;
      loggingLinkCount += 1;
      const operationName = operation.operationName;
      const operationType = operation.query.definitions[0]?.kind === 'OperationDefinition'
        ? operation.query.definitions[0].operation
        : 'unknown';

      // VERBOSE DEBUGGING - Log every query/mutation
      console.log(`[GraphQL Debug] Query #${currentRequestNum} START:`, {
        operation: operationName,
        type: operationType,
        variables: operation.variables,
        context: operation.getContext()
      });

      return forward(operation).map((data) => {
        const duration = Date.now() - startTime;

        // VERBOSE DEBUGGING - Log completion
        console.log(`[GraphQL Debug] Query #${currentRequestNum} COMPLETE:`, {
          operation: operationName,
          type: operationType,
          duration: duration + ' ms',
          hasErrors: !!data.errors,
          errorCount: data.errors?.length || 0
        });

        if (isDev) {
          console.debug(`GraphQL Network Response #${currentRequestNum}`, { query: operationName, duration: duration + ' ms' });
        }
        return data;
      });
    });

    const nextClient = new ApolloClient({
      link: loggingLink.concat(removeTypenameLink).concat(authLink).concat(httpLink),
      cache: new InMemoryCache({
        typePolicies: {
          User: {
            keyFields: ['_id'],
            fields: {
              // Preserve existing non-null values when incoming value is null
              // This prevents cache oscillation when different queries return null vs actual values
              name: {
                merge(existing, incoming) {
                  return incoming ?? existing;
                }
              }
            }
          },
          Request: { fields: { blocks: { merge: (_, incoming) => incoming } } },
          RequestBlock: { keyFields: false },
          RequestBlockTemplate: { keyFields: false },
          PaginatedRequests: { keyFields: ['includeArchived', 'page', 'status'] }
        }
      }),
      connectToDevTools: isDev
    });

    console.log('[GraphQL Debug] New Apollo Client created with fresh cache');
    console.log('[GraphQL Debug] Setting client state:', {
      isAuthenticated,
      previousClientExists: !!client,
      cacheSize: 0 // Fresh cache
    });

    setClient(nextClient);
    setClientIsAuthenticated(isAuthenticated);

    console.log('[GraphQL Debug] ========== End Client Recreation ==========\n');
  }, [handleLogout, isAuthenticated, getAccessTokenSilently, isLoading]);

  // VERBOSE DEBUGGING - Track render conditions
  const shouldShowLoader = isLoading || !client || !requiresAuthClient || (isAuthenticated && !clientIsAuthenticated);

  if (shouldShowLoader) {
    const reasons = [];
    if (isLoading) reasons.push('isLoading=true');
    if (!client) reasons.push('no client');
    if (!requiresAuthClient) reasons.push('requiresAuthClient=null');
    if (isAuthenticated && !clientIsAuthenticated) reasons.push('auth mismatch');

    console.log('[GraphQL Debug] Showing Loader. Reasons:', reasons.join(', '), {
      isLoading,
      hasClient: !!client,
      requiresAuthClient,
      isAuthenticated,
      clientIsAuthenticated
    });

    return <Loader />;
  }

  console.log('[GraphQL Debug] Rendering ApolloProvider with client');
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export const GraphqlProvider = () => {
  return (
    <RIApolloProvider>
      <Outlet />
    </RIApolloProvider>
  );
};
